import { useQueryClient } from "@tanstack/react-query";

import type { Route } from "./+types/$locale.product.$id";
import { getProduct, getProductBySlug } from "@/api/catalog";
import { buildSeoMeta } from "@/components/seo/meta";
import { ProductDetail } from "@/features/catalog/routes/ProductDetail";
import { env } from "@/lib/env";

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Build-time loader: fetches the product so the route's `meta` export can
 * build SEO tags from the same data — no runtime client fetch needed for head
 * metadata when the page is prerendered.
 *
 * Accepts BOTH slugs (canonical) and GUIDs (legacy fallback for products
 * without slugs set in the backoffice). The catalog client-nav still links
 * by GUID when a product has no slug; this loader has to honour that or the
 * navigation crashes (was the regression behind users bouncing back to home).
 *
 * The fetch deliberately omits locationId/date params (a build-time loader has
 * no access to user state). The runtime `useProductByIdOrSlug` hook in
 * `<ProductDetail>` re-queries with the user's selected location/dates when
 * they change; we seed the cache with the location-less response so the first
 * paint has product data without a network round-trip.
 */
export async function loader({ params }: Route.LoaderArgs) {
    const identifier = params.id as string;

    // Prerender sentinel (see react-router.config.ts): must resolve WITHOUT
    // touching the API, and with a 200. The GUID fallback below would otherwise
    // hit an unmatched API route (which answers 401) — and the prerenderer fails
    // the build on ANY non-2xx data response, 404s included. A null product
    // renders the shell; meta and the component both handle it.
    if (identifier === "__prerender_sentinel__") {
        return { product: null };
    }

    const isGuid = GUID_RE.test(identifier);

    // Try the matching variant first; on a clean 404 fall back to the other.
    // (handles slug values that happen to look like GUIDs and vice versa).
    // Both API helpers throw an ApiError with status 404 when the backend
    // returns 404, so we have to catch rather than null-check.
    const tryFetch = async (
        fn: () => Promise<unknown | null>,
    ): Promise<unknown | null> => {
        try {
            return await fn();
        } catch (err) {
            const status = (err as { status?: number } | null)?.status;
            if (status === 404) return null;
            throw err;
        }
    };

    let product: unknown | null = await tryFetch(
        isGuid
            ? () => getProduct(identifier)
            : () => getProductBySlug(identifier),
    );
    if (!product) {
        product = await tryFetch(
            isGuid
                ? () => getProductBySlug(identifier)
                : () => getProduct(identifier),
        );
    }

    if (!product) {
        throw new Response("Product not found", { status: 404 });
    }
    return { product };
}

export function meta({ loaderData, params, location }: Route.MetaArgs) {
    if (!loaderData?.product) return [{ title: "Product not found" }];
    const p = loaderData.product;
    const locale = (params.locale as string) ?? "en";
    const slug = p.slug ?? p.id;
    return buildSeoMeta({
        title: `${p.name} — Rent Baby Equipment`,
        description:
            p.shortDescription ??
            `Rent ${p.name} - premium baby equipment rental with flexible dates and affordable pricing.`,
        path: location.pathname.replace(new RegExp(`^/${locale}`), "") || "/",
        image: p.primaryImageUrl ?? undefined,
        imageAlt: p.name,
        type: "product",
        keywords: `${p.name}, baby equipment rental, ${(p.tags ?? []).join(", ") || "stroller rental, baby gear"}`,
        // TODO Phase 6: hydrate availableLocales from root loader manifest
        locale,
        availableLocales: [{ code: "en" }],
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "Product",
            name: p.name,
            description: p.shortDescription,
            image: p.primaryImageUrl,
            brand: {
                "@type": "Brand",
                name: p.brand || "Stork Rentals",
            },
            offers: {
                "@type": "Offer",
                priceCurrency: "EUR",
                price: p.price1Day ?? 0,
                availability: p.isAvailableForRental
                    ? "https://schema.org/InStock"
                    : "https://schema.org/OutOfStock",
                url: `${env.SITE_URL}/${locale}/product/${slug}`,
            },
        },
    });
}

export default function ProductRoute({ loaderData }: Route.ComponentProps) {
    const queryClient = useQueryClient();
    // Seed the TanStack Query cache so the existing useProductByIdOrSlug hook
    // in ProductDetail returns the loader data on first render with no fetch.
    // Seed BOTH the slug- and id-keyed shapes (mirrors both branches of
    // useProductByIdOrSlug in app/hooks/useCatalog.ts at build-time defaults)
    // so whichever side the runtime picks up (slug fast-path or GUID fallback)
    // has hydrated data.
    // Null on prerender-sentinel paths: seed nothing and render the shell.
    // `loaderData` can also legitimately be `undefined` at runtime: ssr:false
    // loaders only ever run at BUILD time, for the concrete paths
    // react-router.config.ts's prerender() enumerated (see CLAUDE.md, and the
    // identical guard in $locale.catalogue.$locationSlug.tsx). A product
    // created after the last deploy has no static loader output — the CDN
    // serves the SPA-fallback shell and this route mounts with no loaderData
    // at all. Reading `loaderData.product` unguarded crashes right here (React
    // hydration error #418); `?.` makes that case fall through to
    // <ProductDetail>'s own client-side loading/not-found state instead of
    // throwing.
    const product = (loaderData?.product ?? null) as { slug?: string | null; id: string } | null;
    if (product) {
        // Seed-once: mirrors $locale.locations._index.tsx post-fecceb2c6 — this
        // runs on every render (component body, not an effect), and
        // useProductByIdOrSlug can already have written fresher data into this
        // key by the time a later render fires. `(old) => old ?? product` only
        // fills the cache when it's genuinely empty, so a re-render never
        // clobbers live data with the build-time snapshot.
        queryClient.setQueryData(
            ["product", product.id, undefined, undefined, undefined],
            (old: typeof product | undefined) => old ?? product,
        );
        if (product.slug) {
            queryClient.setQueryData(
                ["product", "slug", product.slug, undefined, undefined, undefined],
                (old: typeof product | undefined) => old ?? product,
            );
        }
    }
    return <ProductDetail />;
}
